import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { FaTimes, FaTrash, FaCheckCircle, FaExclamationCircle } from 'react-icons/fa';
import { ProductSubscriptionApiService } from '../../../service/seller/productSubscriptionApiService';
import { CategoryApiService } from '../../../service/website/categoryApiService';
import { useLoader } from '../../../base/context/LoaderProvider';
import { ToastTypes, useMessage } from '../../../base/context/MessageProvider';
import CategoryDropdown from '../../../website/component/common/CategoryDropdown';
import '../../../website/component/common/SubscriptionModal/style.css';

const UnmappedProductsModal = ({ show, onHide, onRefresh,  selectedSKUs = [], allProducts = [] }) => {
  console.log('UnmappedProductsModal props:', { show, selectedSKUs: selectedSKUs.length, allProducts: allProducts.length });
  const loader = useLoader();
  const message = useMessage();

  const [selectedProductsByKeyword, setSelectedProductsByKeyword] = useState({});
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [keywordCategories, setKeywordCategories] = useState({});
  const [isMapping, setIsMapping] = useState(false);

  // --- NEW: cache + preloaded categories + browse tree ---
  const [allCategories, setAllCategories] = useState([]);
  const [categoryCache, setCategoryCache] = useState({});
  const [browseTree, setBrowseTree] = useState(null);

  const tableBySku = useMemo(() => {
    const m = new Map();
    (allProducts || []).forEach(p => {
      if (p?.sku) m.set(p.sku, p);
      if (p?.offerId) m.set(p.offerId, p);
    });
    return m;
  }, [allProducts]);

  const tableById = useMemo(() => {
    const m = new Map();
    (allProducts || []).forEach(p => {
      const id = p?._id || p?.id;
      if (id) m.set(id, p);
    });
    return m;
  }, [allProducts]);

  // --- NEW: normalize/token helpers ---
  const normalizeText = (s = '') =>
    s
      .toLowerCase()
      .normalize('NFKD').replace(/[\u0300-\u036f]/g, '') // strip diacritics
      .replace(/[>]/g, ' ')                               // '>' -> space
      .replace(/[^a-z0-9\s]/g, ' ')                       // punctuation -> space
      .replace(/\s+/g, ' ')
      .trim();

  const tokenize = (s = '') => normalizeText(s).split(' ').filter(Boolean);

  const uniqueById = (arr = []) => {
    const seen = new Set();
    return arr.filter(it => {
      const id = it?.id || it?._id;
      if (!id || seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  };

  // --- NEW: flatten full category tree once for fast local search ---
  const flattenCategories = (cats, parentPath = '') => {
    const out = [];
    const walk = (nodes, parent) => {
      (nodes || []).forEach(cat => {
        const id = cat.c_id || cat._id || cat.id;
        const name = cat.c_name || cat.name || cat.categoryName;
        if (!id || !name) return;
        const children = cat.children || cat.subCategories || cat.subcategories;
        const path = parent ? `${parent} > ${name}` : name;
        out.push({ id, name, path, searchText: normalizeText(path) });
        if (children?.length) walk(children, path);
      });
    };
    walk(cats, parentPath);
    return out;
  };



  // Tabs: active keyword + horizontal scroller
  const keywordGroups = useMemo(
    () => Object.keys(selectedProductsByKeyword),
    [selectedProductsByKeyword]
  );
  const [activeTabKeyword, setActiveTabKeyword] = useState('');
  const scrollerRef = useRef(null);
  const scrollProducts = (dir = 1) => {
    const node = scrollerRef.current;
    if (!node) return;
    const card = node.querySelector('.product-item');
    const step = card ? card.getBoundingClientRect().width + 16 : 300;
    node.scrollBy({ left: dir * step, behavior: 'smooth' });
  };

  const fetchUnmappedProductsByKeywords = useCallback(() => {
    if (!show) return;

    loader.showLoading(true);

    ProductSubscriptionApiService.getSearchKeywords()
      .then((keywordsRes) => {
        loader.showLoading(false);
        if (keywordsRes.status.code === 200) {
          const keywords = Array.isArray(keywordsRes.data.keywords) ? keywordsRes.data.keywords : [];
          const productsByKeyword = {};

          // Only keep unmapped products
          keywords.forEach(keyword => {
            if (Array.isArray(keyword.productIds) && keyword.productIds.length) {
             // const keywordProducts = keyword.productIds.filter(p => !p.c_id && !p.categoryId && !(p?.categoryPath && String(p.categoryPath).trim()));
              
              const keywordProducts = keyword.productIds.filter(p => {
                const keySku = p?.sku || p?.offerId;
                const keyId  = p?._id || p?.id;
                const t = tableBySku.get(keySku) || tableById.get(keyId);
                
                // Check multiple indicators of mapping
                const hasCategory = (t?.categoryId && String(t.categoryId).trim()) || 
                                   (p?.categoryId && String(p.categoryId).trim());
                const hasCategoryName = (t?.c_name && String(t.c_name).trim()) || 
                                       (p?.c_name && String(p.c_name).trim());
                const hasCategoryPath = (t?.categoryPath && String(t.categoryPath).trim()) || 
                                       (p?.categoryPath && String(p.categoryPath).trim());
                
                // Product is mapped if it has any category indicator
                const isMapped = hasCategory || hasCategoryName || hasCategoryPath;
                
                // Return unmapped products (inverse of isMapped)
                return !isMapped;
              });
             
             if (keywordProducts.length) {
                productsByKeyword[keyword.searchKey] = keywordProducts;
              }
            }
          });

          console.log('Fetched productsByKeyword:', productsByKeyword);
          console.log('Total keyword groups:', Object.keys(productsByKeyword).length);
          
          setSelectedProductsByKeyword(productsByKeyword);
          // Set default active tab
          const keys = Object.keys(productsByKeyword);
          console.log('Setting active tab to:', keys[0] || 'none');
          setActiveTabKeyword(keys[0] || '');
        }
      })
      .catch((err) => {
        loader.showLoading(false);
        console.error('Error fetching unmapped products:', err);
        message.showToast('Error loading products', ToastTypes.ERROR);
      });
  }, [loader, message, show]);

// ---------- ADDED: Build one tab per selected product (product1, product2, ...) ----------
const buildSelectedUnmappedGroup = useCallback(() => {
  try {
    const selectedSkuSet = new Set((selectedSKUs || []).map(s => s.sku));
    const selectedUnmapped = (allProducts || []).filter(p => {
      const sku = p?.sku || p?.offerId;
      
      // Check multiple indicators of mapping (same logic as keyword filtering)
      const hasCategory = p?.categoryId && String(p.categoryId).trim();
      const hasCategoryName = p?.c_name && String(p.c_name).trim();
      const hasCategoryPath = p?.categoryPath && String(p.categoryPath).trim();
      
      // Product is mapped if it has any category indicator
      const isMapped = hasCategory || hasCategoryName || hasCategoryPath;
      const unmapped = !isMapped;
      
      return sku && selectedSkuSet.has(sku) && unmapped;
    });

    console.log('buildSelectedUnmappedGroup - selectedSkuSet:', selectedSkuSet);
    console.log('buildSelectedUnmappedGroup - allProducts count:', (allProducts || []).length);
    console.log('buildSelectedUnmappedGroup - selectedUnmapped count:', selectedUnmapped.length);
    
    if (selectedUnmapped.length > 0) {
      const groups = {};
      let idx = 1;
      selectedUnmapped.forEach(prod => {
        const tabName = `product${idx++}`; // product1, product2, ...
        groups[tabName] = [prod];          // one product per tab
      });
      console.log('buildSelectedUnmappedGroup - returning groups:', groups);
      return groups; // returns multiple tabs instead of a single "Selected" group
    }
  } catch (e) {
    console.error('buildSelectedUnmappedGroup error:', e);
  }
  return null;
}, [selectedSKUs, allProducts]);


  // --- NEW: load all categories once (used for frontend fallback & browse cache) ---
  const loadAllCategories = useCallback(async () => {
    try {
      const res = await CategoryApiService.getCategories();
      if (res?.status?.code === 200 && Array.isArray(res.data)) {
        setBrowseTree(res.data); // cache for inline browse
        setAllCategories(flattenCategories(res.data)); // flattened for local search
      }
    } catch (e) {
      console.error('loadAllCategories error:', e);
    }
  }, []);


  const searchCategories = async (searchTerm) => {
    if (!searchTerm || searchTerm.trim().length < 1) {
      setCategories([]);
      setLoadingCategories(false);
      return;
    }

    const cacheKey = searchTerm.trim();
    if (categoryCache[cacheKey]) {
      setCategories(categoryCache[cacheKey]);
      setLoadingCategories(false);
      return;
    }

    setLoadingCategories(true);
    try {
      const res = await CategoryApiService.searchCategoriesByKeyword(searchTerm);
      let formatted = [];
      if (res.status?.code === 200 && res.data) {
        formatted = res.data.map(category => ({
          id: category.c_id || category.id,
          name: category.c_name || category.name,
          path: category.path,
          searchText: normalizeText(category.path || '')
        }));
      }

      // 2) Frontend fallback (order-agnostic AND tokens on full path)
      const tokens = tokenize(searchTerm); // e.g., "men watch" -> ["men","watch"]
      const fallback = allCategories.filter(c =>
        tokens.every(t => c.searchText.includes(t))
      );

      // 3) Merge + de-dupe (backend first)
      const merged = uniqueById([...(formatted || []), ...fallback]);

      setCategoryCache(prev => ({ ...prev, [cacheKey]: merged }));
      setCategories(merged);
    } catch (error) {
      console.error('Error searching categories:', error);
      // Even if backend fails, try pure-frontend
      const tokens = tokenize(searchTerm);
      const fallback = allCategories.filter(c =>
        tokens.every(t => c.searchText.includes(t))
      );
      setCategories(fallback);
    } finally {
      setLoadingCategories(false);
    }
  };
  // Inline browse loader for CategoryDropdown (cached)
  const loadBrowseTree = async () => {
    if (browseTree) return browseTree; // reuse cache
    try {
      const res = await CategoryApiService.getCategories();
      if (res?.status?.code === 200 && Array.isArray(res.data)) {
        setBrowseTree(res.data);
        if (allCategories.length === 0) {
          setAllCategories(flattenCategories(res.data));
        }
        return res.data;
      }
    } catch { }
    return [];
  };

  const handleCategorySelect = (keyword, categoryId, categoryName) => {
    setKeywordCategories(prev => ({
      ...prev,
      [keyword]: { categoryId, categoryName }
    }));
  };



  const handleRemoveProduct = (keyword, productId) => {
    setSelectedProductsByKeyword(prev => {
      const next = { ...prev };
      if (next[keyword]) {
        next[keyword] = next[keyword].filter(p => (p._id || p.id) !== productId);
        if (next[keyword].length === 0) {
          delete next[keyword];
          // If active tab got removed, move to first available
          const keys = Object.keys(next);
          if (activeTabKeyword === keyword) setActiveTabKeyword(keys[0] || '');
        }
      }
      return next;
    });
  };

  const handleRemoveKeywordGroup = (keyword) => {
    setSelectedProductsByKeyword(prev => {
      const next = { ...prev };
      delete next[keyword];
      const keys = Object.keys(next);
      if (activeTabKeyword === keyword) setActiveTabKeyword(keys[0] || '');
      return next;
    });

    setKeywordCategories(prev => {
      const next = { ...prev };
      delete next[keyword];
      return next;
    });
  };

  const handleSubscribeNow = async () => {
    const keywordGroups = Object.keys(selectedProductsByKeyword);

    if (keywordGroups.length === 0) {
      message.showToast('No products available for mapping.', ToastTypes.ERROR);
      return;
    }

    const missingCategories = keywordGroups.filter(keyword => !keywordCategories[keyword]?.categoryId);
    if (missingCategories.length > 0) {
      message.showToast(`Please select categories for: ${missingCategories.join(', ')}`, ToastTypes.ERROR);
      return;
    }

    setIsMapping(true);

    const promises = [];
    keywordGroups.forEach(keyword => {
      const categoryData = keywordCategories[keyword];
      const products = selectedProductsByKeyword[keyword];

      products.forEach(product => {
        promises.push(
          ProductSubscriptionApiService.mapProductCategory(product._id || product.id, categoryData.categoryId)
        );
      });
    });

    try {
      const results = await Promise.all(promises);
      const successCount = results.filter(res => res.status?.code === 200).length;

      if (successCount > 0) {
        message.showToast(`Successfully mapped ${successCount} products to categories`, ToastTypes.SUCCESS);
        setSelectedProductsByKeyword({});
        setKeywordCategories({});
        if (onRefresh) onRefresh();
        onHide();
      } else {
        message.showToast("Failed to map products to categories", ToastTypes.ERROR);
      }
    } catch (err) {
      message.showToast(err?.message || "Server error. Try again later", ToastTypes.ERROR);
    } finally {
      setIsMapping(false);
    }
  };

  // --- NEW: on open, preload categories once for local fallback search ---
  useEffect(() => {
    if (show && allCategories.length === 0) {
      loadAllCategories();
    }
  }, [show, allCategories.length, loadAllCategories]);


 useEffect(() => {
  if (show) {
    console.log('Modal opened, selectedSKUs:', selectedSKUs);
    
    // Try selected group first
    const selectedGroup = buildSelectedUnmappedGroup();
    console.log('selectedGroup result:', selectedGroup);
    
    if (selectedGroup) {
      console.log('Using selected group with keys:', Object.keys(selectedGroup));
      setSelectedProductsByKeyword(selectedGroup);
      const firstKey = Object.keys(selectedGroup)[0] || '';
      setActiveTabKeyword(firstKey);
    } else {
      console.log('No selected group, trying keywords');
      fetchUnmappedProductsByKeywords();
    }
  }
}, [show]);

  // Fallback: if no keyword groups found after fetch, show all unmapped
  useEffect(() => {
    if (show && Object.keys(selectedProductsByKeyword).length === 0) {
      const timer = setTimeout(() => {
        console.log('No keyword groups found, creating fallback with all unmapped products');
        const allUnmapped = (allProducts || []).filter(p => {
          const hasCategory = p?.categoryId && String(p.categoryId).trim();
          const hasCategoryName = p?.c_name && String(p.c_name).trim();
          const hasCategoryPath = p?.categoryPath && String(p.categoryPath).trim();
          const isMapped = hasCategory || hasCategoryName || hasCategoryPath;
          return !isMapped;
        });
        
        if (allUnmapped.length > 0) {
          const fallbackGroup = { 'unmapped': allUnmapped };
          console.log('Created fallback group with', allUnmapped.length, 'products');
          setSelectedProductsByKeyword(fallbackGroup);
          setActiveTabKeyword('unmapped');
        }
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [show, selectedProductsByKeyword, allProducts]);

  useEffect(() => {
    if (!show) {
      setKeywordCategories({});
      setCategories([]);
      setLoadingCategories(false);
    }
  }, [show]);

  const getTotalProductCount = () =>
    Object.values(selectedProductsByKeyword).reduce((t, items) => t + items.length, 0);

  const isSubscribeDisabled = () => {
    const keywordGroups = Object.keys(selectedProductsByKeyword);
    if (keywordGroups.length === 0) return true;
    return keywordGroups.some(keyword => !keywordCategories[keyword]?.categoryId);
  };

  const truncateText = (text, maxLength = 40) =>
    !text ? '' : text.length > maxLength ? text.substring(0, maxLength) + '…' : text;


  if (!show) return null;

  // Active tab’s data
  const activeProducts = activeTabKeyword ? (selectedProductsByKeyword[activeTabKeyword] || []) : [];
  const activeCategoryData = activeTabKeyword ? keywordCategories[activeTabKeyword] : undefined;

  return (
    <div className="modern-modal-overlay" onClick={onHide}>
      <div className="modern-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modern-modal-header">
          <div className="modal-header-content">
            <h2 className="modal-title">Map Product Categories</h2>
            <p className="modal-subtitle">
              {getTotalProductCount()} products selected across {keywordGroups.length} group{keywordGroups.length > 1 ? 's' : ''}
            </p>
          </div>
          <button className="modern-close-btn" onClick={onHide} disabled={isMapping} aria-label="Close">
            <FaTimes />
          </button>
        </div>

        {/* Tabs (top, horizontal scroll) */}
        <div className="tabs-container" role="tablist" aria-label="Keyword Tabs">
          {keywordGroups.length === 0 ? (
            <div className="tabs-empty-placeholder">No groups</div>
          ) : (
            keywordGroups.map((kw) => {
              const count = (selectedProductsByKeyword[kw] || []).length;
              const isActive = kw === activeTabKeyword;
              const isChecked = !!keywordCategories[kw]?.categoryId;
              const isUnchecked = !isChecked;
              return (
                <button
                  key={kw}
                  role="tab"
                  aria-selected={isActive}
                  aria-controls={`panel-${kw}`}
                  id={`tab-${kw}`}
                  className={`tab-item ${isActive ? 'tab-item-active' : ''} ${isChecked ? 'tab-item-checked' : ''} ${isUnchecked ? 'tab-item-unchecked' : ''}`}
                  onClick={() => setActiveTabKeyword(kw)}
                  disabled={isMapping}
                  title={`Show products for "${kw}"`}
                >
                  {isChecked ? (
                    <FaCheckCircle className="tab-tick" aria-hidden="true" />
                  ) : (
                    <FaExclamationCircle className="tab-warn" aria-hidden="true" />
                  )}
                  <span className="tab-title">"{kw}"</span>
                  <span className="tab-badge">{count}</span>
                </button>
              );
            })
          )}
        </div>


        <div className="modern-modal-body">
          {keywordGroups.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📦</div>
              <h3>No unmapped products found</h3>
              <p>All your products have categories assigned.</p>
            </div>
          ) : (
            <div
              id={`panel-${activeTabKeyword}`}
              role="tabpanel"
              aria-labelledby={`tab-${activeTabKeyword}`}
            >
              {/* Keyword header with remove group */}
              <div className="keyword-header">
                <div className="keyword-info">
                  <h3 className="keyword-title">"{activeTabKeyword}"</h3>
                  <span className="product-count-badge">
                    {activeProducts.length} product{activeProducts.length > 1 ? 's' : ''}
                  </span>
                </div>
                <button
                  className="remove-group-btn"
                  onClick={() => handleRemoveKeywordGroup(activeTabKeyword)}
                  title={`Remove all products for "${activeTabKeyword}"`}
                >
                  <FaTrash />
                </button>
              </div>

              {/* Category selection (search + inline browse exactly like website) */}
              <div className="category-section">
                <label className="category-label">
                  Select Category for "{activeTabKeyword}" products:
                </label>
                <p className="category-instruction">
                  Search and select the most appropriate category for these products.
                </p>
                <CategoryDropdown
                  categories={categories}
                  selectedCategoryId={activeCategoryData?.categoryId || ''}
                  selectedCategoryPath={activeCategoryData?.categoryName || ''}
                  onCategorySelect={(categoryId, categoryName) =>
                    handleCategorySelect(activeTabKeyword, categoryId, categoryName)
                  }
                  onSearch={searchCategories}
                  loading={loadingCategories}
                  placeholder="Search and select a category..."
                  disabled={isMapping}
                  // Enable inline browse panel
                  loadBrowseTree={loadBrowseTree}
                />
                {/* {activeCategoryData?.categoryId && (
                  <div className="category-confirmation">
                    <FaCheckCircle className="check-icon" />
                    <span>Selected: {activeCategoryData.categoryName}</span>
                  </div>
                )} */}
              </div>

              {/* PRODUCTS — HORIZONTAL SCROLLER (same as website) */}
              <div className="products-row-wrap">
                <button
                  type="button"
                  className="scroll-btn scroll-btn-left"
                  aria-label="Scroll left"
                  onClick={() => scrollProducts(-1)}
                >
                  ‹
                </button>
                <div className="products-scroller" ref={scrollerRef}>
                  {activeProducts.map((product, index) => {
                    const productId = product._id || product.id;
                    const sku = product.sku || product.offerId;
                    const img = (product.imgUrl?.[0] || '').replace(/^http:/, 'https:');
                    const price = product.sp || product.price;
                    return (
                      <div key={index} className="product-item">
                        <img
                          src={img || '/assets/images/common/logo_no_image.png'}
                          alt="Product"
                          className="product-image"
                          referrerPolicy="no-referrer"
                          onError={(e) => {
                            e.currentTarget.src = '/assets/images/common/logo_no_image.png';
                          }}
                        />
                        <div className="product-details">
                          <h4 className="product-name" title={product.title}>
                            {truncateText(product.title)}
                          </h4>
                          <p className="product-sku">SKU: {sku || 'N/A'}</p>
                          {/* <p className="product-price">₹{price || 'N/A'}</p> */}
                        </div>
                        <button
                          className="remove-product-btn"
                          onClick={() => handleRemoveProduct(activeTabKeyword, productId)}
                          title="Remove this product"
                          aria-label="Remove this product"
                        >
                          <FaTimes />
                        </button>
                      </div>
                    );
                  })}
                </div>

                <button
                  type="button"
                  className="scroll-btn scroll-btn-right"
                  aria-label="Scroll right"
                  onClick={() => scrollProducts(1)}
                >
                  ›
                </button>
              </div>
              {/* /PRODUCTS */}
            </div>

          )}
        </div>

        <div className="modern-modal-footer">
          <button
            className="btn-secondary"
            onClick={onHide}
            disabled={isMapping}
          >
            Cancel
          </button>
          <button
            className="btn-primary"
            onClick={handleSubscribeNow}
            disabled={isMapping || isSubscribeDisabled()}
          >
            {isMapping ? (
              <>
                <div className="btn-spinner"></div>
                Mapping...
              </>
            ) : (
              `Map All (${getTotalProductCount()} products)`
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UnmappedProductsModal;